<template>
  <v-dialog v-model="visible" max-width="1180" persistent scrollable>
    <v-card class="tpl-editor" rounded="lg">
      <header class="tpl-editor__head">
        <span class="tpl-editor__head-icon"><FileText :size="18" /></span>
        <div class="tpl-editor__head-text">
          <h3 class="tpl-editor__title">{{ isEdit ? $gt('Vorlage bearbeiten') : $gt('Neue Vorlage') }}</h3>
          <p class="tpl-editor__sub">
            {{ $gt('Betreff und Text mit Platzhaltern – beim Einfügen im Composer werden sie mit den Daten des Falls, des Standorts und des angemeldeten Mitarbeiters befüllt.') }}
          </p>
        </div>
        <ButtonBase
          color="grey"
          :disabled="saving"
          icon-style
          icon-type="cancel"
          @click="close"
        />
      </header>
      <v-divider />

      <v-card-text class="tpl-editor__body">
        <!-- Stammdaten der Vorlage -->
        <div class="tpl-meta">
          <label class="tpl-field tpl-field--grow">
            <span class="tpl-field__label">{{ $gt('Name der Vorlage') }}</span>
            <input v-model.trim="form.name" class="tpl-input" maxlength="160" :placeholder="$gt('z. B. Erstanschreiben Versicherung')">
          </label>
          <label class="tpl-field">
            <span class="tpl-field__label">{{ $gt('Kategorie') }}</span>
            <select v-model="form.category" class="tpl-input tpl-select">
              <option v-for="c in categories" :key="c.key" :value="c.key">{{ $gt(c.label) }}</option>
            </select>
          </label>
          <!-- Mail RA Polz 13.10.2026: Kasko-Schreiben nur in Kasko-Akten. -->
          <label class="tpl-field">
            <span class="tpl-field__label" :title="$gt('In Akten welcher Fallart die Vorlage angeboten wird')">{{ $gt('Fallart') }}</span>
            <select v-model="form.caseType" class="tpl-input tpl-select" data-testid="template-case-type">
              <option v-for="c in CASE_TYPE_OPTIONS" :key="c.value" :value="c.value">{{ $gt(c.label) }}</option>
            </select>
          </label>
          <label class="tpl-field">
            <span class="tpl-field__label">{{ $gt('Reichweite') }}</span>
            <select v-model="form.scope" class="tpl-input tpl-select" :disabled="!canManageShared">
              <option value="personal">{{ $gt('Persönlich (nur ich)') }}</option>
              <option v-if="canManageShared" value="company">{{ $gt('Unternehmen') }}</option>
              <option v-if="canManageShared" value="organization">{{ $gt('Organisation (alle)') }}</option>
            </select>
          </label>
          <label v-if="form.scope === 'company'" class="tpl-field">
            <span class="tpl-field__label">{{ $gt('Unternehmen') }}</span>
            <select v-model="form.idCompany" class="tpl-input tpl-select">
              <option v-for="c in companies" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </label>
        </div>

        <div class="tpl-grid">
          <!-- ── Editor ── -->
          <div class="tpl-col">
            <div class="tpl-col__label"><PenLine :size="12" /> {{ $gt('Editor') }}</div>

            <label class="tpl-field">
              <span class="tpl-field__label">{{ $gt('Betreff') }}</span>
              <input
                ref="subjectRef"
                v-model="form.subject"
                class="tpl-input"
                maxlength="500"
                :placeholder="$gt('Betreff mit Platzhaltern, z. B. Unfall vom {{unfalldatum}} – Az. {{aktenzeichen}}')"
                @focus="activeTarget = 'subject'"
              >
            </label>

            <div class="tpl-body" @focusin="activeTarget = 'body'">
              <MessageComposer
                ref="composerRef"
                v-model="form.bodyHtml"
                class="tpl-composer"
                :placeholder="$gt('Text der Vorlage…')"
              />
            </div>

            <div class="tpl-tokens">
              <div class="tpl-tokens__head">
                <span class="tpl-tokens__label">
                  {{ $gt('Platzhalter einfügen') }}
                  <em class="tpl-tokens__target">
                    {{ activeTarget === 'subject' ? $gt('→ in den Betreff') : $gt('→ in den Text') }}
                  </em>
                </span>
                <button class="tpl-link" type="button" @click="showImport = true">
                  <ClipboardPaste :size="13" />
                  {{ $gt('Aus Text/HTML einfügen') }}
                </button>
              </div>
              <div v-for="group in placeholderGroups" :key="group.key" class="tpl-tokens__group">
                <span class="tpl-tokens__group-label">{{ $gt(group.label) }}</span>
                <div class="tpl-tokens__chips">
                  <button
                    v-for="p in group.items"
                    :key="p.key"
                    class="tpl-chip"
                    :class="{ 'is-used': usedKeys.has(p.key) }"
                    :title="'{{' + p.key + '}}'"
                    type="button"
                    @click="insertPlaceholder(p.key)"
                    @mousedown.prevent
                  >
                    <component :is="usedKeys.has(p.key) ? Check : Plus" :size="12" />
                    {{ $gt(p.label) }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Live-Vorschau ── -->
          <div class="tpl-col">
            <div class="tpl-col__label tpl-col__label--live">
              <span aria-hidden="true" class="tpl-live-dot" />
              {{ $gt('Live-Vorschau mit Beispieldaten') }}
            </div>
            <div class="tpl-preview">
              <div class="tpl-preview__field">
                <span class="tpl-preview__field-label">{{ $gt('Betreff') }}</span>
                <span class="tpl-preview__subject">{{ previewSubject || '–' }}</span>
              </div>
              <div class="tpl-preview__body" v-html="previewBody || emptyPreview" />
            </div>
            <p v-if="unknownKeys.length > 0" class="tpl-warn">
              <TriangleAlert :size="13" />
              {{ $gt('Unbekannte Platzhalter (werden nicht befüllt):') }}
              <code v-for="k in unknownKeys" :key="k">{{ token(k) }}</code>
            </p>
            <p v-else class="tpl-hint">
              <Info :size="13" />
              {{ $gt('Platzhalter, die im konkreten Fall nicht befüllt werden können, bleiben im Composer sichtbar markiert.') }}
            </p>
          </div>
        </div>

        <p v-if="error" class="tpl-error">{{ error }}</p>
      </v-card-text>

      <v-divider />
      <v-card-actions class="d-flex justify-end ma-2">
        <ButtonBase button-text="Abbrechen" :disabled="saving" icon-type="cancel" @click="close" />
        <ButtonBase
          :button-text="isEdit ? 'Änderungen speichern' : 'Vorlage speichern'"
          :disabled="!canSave || saving"
          icon-type="save"
          :loading="saving"
          variant="flat"
          @click="save"
        />
      </v-card-actions>
    </v-card>

    <ImportTextDialog v-model="showImport" @import="onImport" />
  </v-dialog>
</template>

<script setup lang="ts">
  import { Check, ClipboardPaste, FileText, Info, PenLine, Plus, TriangleAlert } from 'lucide-vue-next'
  import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
  import ButtonBase from '@/components/buttons/ButtonBase.vue'
  import MessageComposer from '@/components/messages/MessageComposer.vue'
  import ImportTextDialog from '@/components/messages/templates/ImportTextDialog.vue'
  import {
    createMailTemplate,
    type MailTemplate,
    type MailTemplateCaseType,
    type MailTemplateCategory,
    type MailTemplateInput,
    type MailTemplateScope,
    updateMailTemplate,
  } from '@/services/mailTemplatesApi'
  import { useSnackbarStore } from '@/stores/snackbarStore'
  import {
    extractPlaceholders,
    isKnownPlaceholder,
    MAIL_PLACEHOLDERS,
    type MailTemplateValues,
    PLACEHOLDER_GROUP_LABELS,
    type PlaceholderGroup,
    renderMailTemplateHtml,
    renderMailTemplateSubject,
    SAMPLE_TEMPLATE_VALUES,
  } from '@/utils/mailTemplate'

  export interface MailTemplatePrefill {
    name?: string
    category?: string
    caseType?: MailTemplateCaseType
    subject?: string
    bodyHtml?: string
    scope?: MailTemplateScope
  }

  const props = withDefaults(defineProps<{
    modelValue: boolean
    /** Bestehende Vorlage (Bearbeiten) oder null (Anlegen). */
    template?: MailTemplate | null
    /** Vorbelegung beim Anlegen (Duplizieren, "Als Vorlage speichern", Import). */
    prefill?: MailTemplatePrefill | null
    categories: MailTemplateCategory[]
    canManageShared: boolean
    companies?: Array<{ id: number, name: string }>
    defaultCompanyId?: number | null
    /** Werte für die Vorschau (Vorgabe: Beispieldaten). */
    previewValues?: MailTemplateValues | null
  }>(), {
    template: null,
    prefill: null,
    companies: () => [],
    defaultCompanyId: null,
    previewValues: null,
  })

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void
    (e: 'saved', template: MailTemplate): void
  }>()

  const visible = computed({
    get: () => props.modelValue,
    set: value => emit('update:modelValue', value),
  })

  const isEdit = computed(() => !!props.template)

  /** Wählbare Fallarten einer Vorlage ("" = alle). */
  const CASE_TYPE_OPTIONS: ReadonlyArray<{ value: MailTemplateCaseType, label: string }> = [
    { value: '', label: 'Alle Fallarten' },
    { value: 'kasko', label: 'Nur Kasko' },
    { value: 'haftpflicht', label: 'Nur Haftpflicht' },
  ]

  const form = reactive({
    name: '',
    category: 'sonstiges',
    caseType: '' as MailTemplateCaseType,
    scope: 'personal' as MailTemplateScope,
    idCompany: null as number | null,
    subject: '',
    bodyHtml: '',
  })

  const saving = ref(false)
  const error = ref('')
  const showImport = ref(false)
  const activeTarget = ref<'subject' | 'body'>('body')
  const subjectRef = ref<HTMLInputElement | null>(null)
  const composerRef = ref<InstanceType<typeof MessageComposer> | null>(null)

  function resetForm () {
    const t = props.template
    const p = props.prefill
    form.name = t?.name ?? p?.name ?? ''
    form.category = t?.category ?? p?.category ?? props.categories[0]?.key ?? 'sonstiges'
    form.caseType = t?.caseType ?? p?.caseType ?? ''
    form.scope = t?.scope ?? p?.scope ?? 'personal'
    if (!props.canManageShared) form.scope = 'personal'
    form.idCompany = t?.idCompany ?? props.defaultCompanyId ?? props.companies[0]?.id ?? null
    form.subject = t?.subject ?? p?.subject ?? ''
    form.bodyHtml = t?.bodyHtml ?? p?.bodyHtml ?? ''
    error.value = ''
    activeTarget.value = 'body'
    nextTick(() => {
      const el = composerRef.value?.editorEl?.() as HTMLElement | null
      if (el && el.innerHTML !== form.bodyHtml) el.innerHTML = form.bodyHtml
    })
  }

  watch(() => props.modelValue, open => {
    if (open) resetForm()
  })

  watch(() => form.scope, scope => {
    if (scope === 'company' && form.idCompany == null) {
      form.idCompany = props.defaultCompanyId ?? props.companies[0]?.id ?? null
    }
  })

  // ── Platzhalter ──────────────────────────────────────────────────────
  const placeholderGroups = computed(() => {
    const order: PlaceholderGroup[] = ['fall', 'nutzer', 'standort', 'allgemein']
    return order.map(key => ({
      key,
      label: PLACEHOLDER_GROUP_LABELS[key],
      items: MAIL_PLACEHOLDERS.filter(p => p.group === key),
    }))
  })

  const usedKeys = computed(() => new Set(extractPlaceholders(`${form.subject} ${form.bodyHtml}`)))

  /** Darstellung eines Platzhalters mit Klammern (im Template nicht direkt möglich). */
  function token (key: string): string {
    return `{{${key}}}`
  }
  const unknownKeys = computed(() => [...usedKeys.value].filter(k => !isKnownPlaceholder(k)))

  // Cursorposition im Editor merken (Chip-Klick nimmt den Fokus).
  let savedRange: Range | null = null

  function rememberSelection () {
    const sel = window.getSelection()
    const el = composerRef.value?.editorEl?.() as HTMLElement | null
    if (!sel || sel.rangeCount === 0 || !el) return
    const range = sel.getRangeAt(0)
    if (el.contains(range.commonAncestorContainer)) savedRange = range.cloneRange()
  }

  onMounted(() => document.addEventListener('selectionchange', rememberSelection))
  onBeforeUnmount(() => document.removeEventListener('selectionchange', rememberSelection))

  function insertPlaceholder (key: string) {
    const token = `{{${key}}}`
    if (activeTarget.value === 'subject') {
      const input = subjectRef.value
      const start = input?.selectionStart ?? form.subject.length
      const end = input?.selectionEnd ?? start
      const before = form.subject.slice(0, start)
      const after = form.subject.slice(end)
      const sep = before && !/\s$/.test(before) ? ' ' : ''
      form.subject = `${before}${sep}${token}${after}`
      nextTick(() => {
        if (!input) return
        const pos = before.length + sep.length + token.length
        input.focus()
        input.setSelectionRange(pos, pos)
      })
      return
    }
    const el = composerRef.value?.editorEl?.() as HTMLElement | null
    if (!el) {
      form.bodyHtml += token
      return
    }
    el.focus()
    const sel = window.getSelection()
    if (sel && savedRange && el.contains(savedRange.commonAncestorContainer)) {
      sel.removeAllRanges()
      sel.addRange(savedRange)
    } else if (sel) {
      const range = document.createRange()
      range.selectNodeContents(el)
      range.collapse(false)
      sel.removeAllRanges()
      sel.addRange(range)
    }
    document.execCommand('insertText', false, token)
    composerRef.value?.syncFromEditor?.()
    rememberSelection()
  }

  function onImport (html: string) {
    const el = composerRef.value?.editorEl?.() as HTMLElement | null
    const hasText = form.bodyHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim()
    form.bodyHtml = hasText ? `${form.bodyHtml}<br><br>${html}` : html
    if (el) el.innerHTML = form.bodyHtml
    activeTarget.value = 'body'
  }

  // ── Vorschau ─────────────────────────────────────────────────────────
  const values = computed<MailTemplateValues>(() => props.previewValues ?? SAMPLE_TEMPLATE_VALUES)
  const previewSubject = computed(() => renderMailTemplateSubject(form.subject, values.value))
  const previewBody = computed(() =>
    renderMailTemplateHtml(form.bodyHtml, values.value, {
      wrapValue: v => `<span class="tpl-var">${v}</span>`,
    }),
  )
  const emptyPreview = '<span class="tpl-preview__empty">Der Text der Vorlage erscheint hier.</span>'

  // ── Speichern ────────────────────────────────────────────────────────
  const canSave = computed(() => {
    if (!form.name.trim()) return false
    const hasBody = form.bodyHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim()
    if (!form.subject.trim() && !hasBody) return false
    if (form.scope === 'company' && !form.idCompany) return false
    return true
  })

  async function save () {
    if (!canSave.value || saving.value) return
    saving.value = true
    error.value = ''
    const input: MailTemplateInput = {
      name: form.name.trim(),
      category: form.category,
      caseType: form.caseType,
      subject: form.subject.trim(),
      bodyHtml: form.bodyHtml,
      scope: form.scope,
      idCompany: form.scope === 'company' ? form.idCompany : null,
      sortOrder: props.template?.sortOrder ?? 0,
      isActive: props.template?.isActive ?? true,
    }
    try {
      const saved = props.template
        ? await updateMailTemplate(props.template.id, input)
        : await createMailTemplate(input)
      useSnackbarStore().showSnackbar({
        message: props.template ? 'Vorlage gespeichert.' : 'Vorlage angelegt.',
        type: 'success',
      })
      emit('saved', saved)
      visible.value = false
    } catch (error_) {
      error.value = (error_ as Error)?.message || 'Die Vorlage konnte nicht gespeichert werden.'
    } finally {
      saving.value = false
    }
  }

  function close () {
    if (saving.value) return
    visible.value = false
  }
</script>

<style scoped lang="scss">
.tpl-editor {
  display: flex;
  flex-direction: column;
  max-height: 92vh;
}

.tpl-editor__head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 22px 14px;
}

.tpl-editor__head-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #7c3aed, #a78bfa);
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.28);
}

.tpl-editor__head-text {
  flex: 1;
  min-width: 0;
}

.tpl-editor__title {
  margin: 0 0 3px;
  font-size: 1.05rem;
  font-weight: 750;
  color: var(--dark-color, #323232);
}

.tpl-editor__sub {
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.45;
  color: var(--grey-color, #5f5f5f);
}

.tpl-editor__body {
  padding: 16px 22px 8px !important;
}

/* ── Stammdaten ── */
.tpl-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.tpl-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 180px;
}

.tpl-field--grow {
  flex: 1 1 260px;
}

.tpl-field__label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--grey-color, #5f5f5f);
}

.tpl-input {
  padding: 8px 10px;
  border: 1px solid var(--border-grey-color, #d0d5dd);
  border-radius: 8px;
  font: inherit;
  font-size: 0.85rem;
  color: var(--dark-color, #323232);
  background: #fff;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: var(--primary-color, #0076e5);
    box-shadow: 0 0 0 3px rgba(0, 118, 229, 0.12);
  }

  &:disabled {
    background: var(--grey-light-color, #f3f5f7);
    color: var(--grey-color, #5f5f5f);
  }
}

.tpl-select {
  cursor: pointer;
}

/* ── Layout ── */
.tpl-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  gap: 22px;
}

@media (max-width: 1000px) {
  .tpl-grid {
    grid-template-columns: 1fr;
  }
}

.tpl-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.tpl-col__label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--grey-color, #5f5f5f);

  svg {
    color: var(--primary-color, #0076e5);
  }
}

.tpl-live-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.18);
}

.tpl-body {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-grey-color, #d0d5dd);
  border-radius: 12px;
  overflow: hidden;
  min-height: 260px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: var(--primary-color, #0076e5);
    box-shadow: 0 0 0 3px rgba(0, 118, 229, 0.12);
  }
}

.tpl-composer {
  min-height: 240px;

  :deep(.mail-editor) {
    min-height: 200px;
    max-height: 360px;
  }
}

/* ── Platzhalter-Chips ── */
.tpl-tokens {
  padding: 10px 12px 6px;
  border: 1px dashed var(--border-grey-color, #d0d5dd);
  border-radius: 12px;
  background: #fbfcfe;
}

.tpl-tokens__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
}

.tpl-tokens__label {
  font-size: 0.74rem;
  font-weight: 700;
  color: var(--dark-color, #323232);
}

.tpl-tokens__target {
  margin-left: 6px;
  font-style: normal;
  font-weight: 500;
  color: var(--primary-color, #0076e5);
}

.tpl-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: none;
  background: transparent;
  padding: 3px 6px;
  border-radius: 6px;
  font-size: 0.76rem;
  font-weight: 600;
  color: var(--primary-color, #0076e5);
  cursor: pointer;

  &:hover {
    background: var(--primary-light-color, #deeefb);
  }
}

.tpl-tokens__group {
  display: grid;
  grid-template-columns: 86px 1fr;
  align-items: start;
  gap: 6px;
  padding: 4px 0;
}

.tpl-tokens__group-label {
  padding-top: 4px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--grey-color, #5f5f5f);
}

.tpl-tokens__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.tpl-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border: 1px solid var(--border-grey-color, #d0d5dd);
  border-radius: 999px;
  background: #fff;
  font-size: 0.72rem;
  color: var(--dark-color, #323232);
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;

  &:hover {
    border-color: var(--primary-color, #0076e5);
    background: var(--primary-light-color, #deeefb);
  }

  &.is-used {
    border-color: #10b981;
    background: #ecfdf5;
    color: #047857;
  }
}

/* ── Vorschau ── */
.tpl-preview {
  border: 1px solid var(--border-grey-color, #d0d5dd);
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
  min-height: 300px;
}

.tpl-preview__field {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border-grey-color-light, #e6eaf0);
  background: var(--grey-light-color, #f3f5f7);
}

.tpl-preview__field-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--grey-color, #5f5f5f);
}

.tpl-preview__subject {
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--dark-color, #323232);
}

.tpl-preview__body {
  padding: 14px 16px;
  font-size: 0.86rem;
  line-height: 1.55;
  max-height: 420px;
  overflow-y: auto;

  :deep(.tpl-var) {
    padding: 0 3px;
    border-radius: 4px;
    background: rgba(16, 185, 129, 0.14);
    color: #047857;
  }

  :deep(.mail-tpl-missing) {
    padding: 0 3px;
    border-radius: 4px;
    background: #fef3c7;
    color: #92400e;
    border-bottom: 2px dotted #f59e0b;
  }

  :deep(.tpl-preview__empty) {
    color: #a6aeb8;
  }
}

.tpl-hint,
.tpl-warn {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  font-size: 0.76rem;
  color: var(--grey-color, #5f5f5f);

  code {
    padding: 1px 5px;
    border-radius: 4px;
    background: #fef3c7;
    color: #92400e;
  }
}

.tpl-warn {
  color: #b45309;
}

.tpl-error {
  margin: 12px 0 0;
  font-size: 0.82rem;
  color: var(--error-color, #ff5252);
}
</style>
